import { observer } from "mobx-react-lite";
import { FC, useEffect } from "react";
import { SidebarExpanded } from "@/components/layout/components/sidebar/sidebar-expanded";
import { SidebarCollapsed } from "@/components/layout/components/sidebar/sidebar-collapsed";
import { motion } from "motion/react";
import { easeInOut } from "motion";
import { SIDEBAR_VARIANTS, SidebarVariant } from "@/components/layout/components/sidebar/constants";
import { SidebarDesktopSection } from "@/components/layout/components/sidebar/sidebar-desktop-section";
import { useAppStore } from "@/store";
import styled from "@emotion/styled";

export interface SidebarProps {
  currentSidebarVariant: SidebarVariant;
  shouldHideSidebarToggle: boolean;
  setIsImportModalOpen: (b: boolean) => void;
  toggleSidebar: () => void;
}

export const Sidebar: FC<SidebarProps> = observer(
  ({ currentSidebarVariant, setIsImportModalOpen, toggleSidebar, shouldHideSidebarToggle }) => {
    const { store } = useAppStore();
    const transition = {
      duration: store.interface.sidebarAnimateNextChange ? 0.3 : 0,
      ease: easeInOut,
    };

    useEffect(() => {
      // reset the "next change" flag after using a "no animation" transition
      if (store.interface.sidebarAnimateNextChange === false) {
        setTimeout(() => {
          store.interface.resetSidebarAnimateNextChange();
        }, 0);
      }
    }, [store.interface]);

    return (
      <StyledMotionDiv
        layout
        initial={false}
        animate={currentSidebarVariant}
        variants={SIDEBAR_VARIANTS}
        transition={transition}
        data-component="Sidebar"
      >
        <SidebarDesktopSection
          toggleSidebar={toggleSidebar}
          currentSidebarVariant={currentSidebarVariant}
          navigateBack={store.navigation.goBack}
          navigateForward={store.navigation.goForward}
          canNavigateBack={store.navigation.canGoBack}
          canNavigateForward={store.navigation.canGoForward}
        />
        {currentSidebarVariant === SidebarVariant.Collapsed ? (
          <SidebarCollapsed
            key="collapsed"
            shouldHideSidebarToggle={shouldHideSidebarToggle}
            setIsImportModalOpen={setIsImportModalOpen}
            toggleSidebar={toggleSidebar}
          />
        ) : (
          <SidebarExpanded
            key="expanded"
            shouldHideSidebarToggle={shouldHideSidebarToggle}
            setIsImportModalOpen={setIsImportModalOpen}
            toggleSidebar={toggleSidebar}
          />
        )}
      </StyledMotionDiv>
    );
  }
);

const StyledMotionDiv = styled(motion.div)`
  overflow: hidden;
  flex-shrink: 0;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
`;
StyledMotionDiv.displayName = "StyledMotionDiv";
